import { QuestionMarkCircleIcon } from '@heroicons/react/solid';

import useLocale from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';
import ReactTextareaAutosize from 'react-textarea-autosize';
import { toast } from 'sonner';

import { VideoModelSelection } from '@/components/videos/VideoModelSelection';
import { VideoOptions } from '@/components/videos/VideoOptions';

import {
  AspectRatioType,
  DurationType,
  getDefaultDurationForModel,
  getDefaultResolutionForModel,
  getMaxPromptLengthForVideoModel,
  getVideoModelCredits,
  ResolutionType,
  videoModelsWithDescription,
  VideoModelType,
} from '@/constants/videoModels';

import Modal from '../atoms/Modal';
import Button from '../buttons/Button';
import ButtonLink from '../links/ButtonLink';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface VideoGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  defaultPrompt: string;
  isFreePlan?: boolean;
}

export const VideoGenerationModal = ({
  isOpen,
  onClose,
  imageUrl,
  defaultPrompt,
  isFreePlan = false,
}: VideoGenerationModalProps) => {
  const { t } = useLocale('ai-images');
  const [prompt, setPrompt] = useState(defaultPrompt);

  // Find default image-to-video model
  const defaultImageModel = useMemo(() => {
    const model = videoModelsWithDescription.find((m) =>
      m.options.inputType.includes('image'),
    );
    return model?.title || 'Runway Gen3 Alpha';
  }, []);

  const [selectedModel, setSelectedModel] =
    useState<VideoModelType>(defaultImageModel);
  const [duration, setDuration] = useState<DurationType>('5');
  const [aspectRatio, setAspectRatio] = useState<AspectRatioType>('16:9');
  const [resolution, setResolution] = useState<ResolutionType>(
    getDefaultResolutionForModel(
      videoModelsWithDescription.find((m) => m.title === selectedModel),
    ),
  );
  const [generateAudio, setGenerateAudio] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState(false);

  const maxPromptLength = useMemo(
    () => getMaxPromptLengthForVideoModel(selectedModel),
    [selectedModel],
  );

  // Get credits for selected model
  const selectedModelCredits = useMemo(() => {
    return getVideoModelCredits(
      selectedModel,
      duration,
      resolution,
      generateAudio,
    );
  }, [selectedModel, duration, resolution, generateAudio]);

  // Effect to update duration, aspect ratio, and resolution when model changes
  useEffect(() => {
    const model = videoModelsWithDescription.find(
      (m) => m.title === selectedModel,
    );
    if (model) {
      // Set default duration
      if (model.options.duration) {
        setDuration(
          getDefaultDurationForModel(model) ?? model.options.duration[0],
        );
      }
      // Set default aspect ratio
      if (model.options.aspectRatio) {
        setAspectRatio(model.options.aspectRatio[0]);
      }
      // Set default resolution
      setResolution(getDefaultResolutionForModel(model));
      // Reset audio toggle
      setGenerateAudio(true);
    }
  }, [selectedModel]);

  const handleGenerate = async () => {
    if (!prompt) {
      return toast.error('Please enter a prompt');
    }

    if (prompt.length > maxPromptLength) {
      return toast.error(
        `Prompt must be ${maxPromptLength} characters or less`,
      );
    }

    setIsGenerating(true);
    try {
      const model = videoModelsWithDescription.find(
        (m) => m.title === selectedModel,
      );

      const response = await fetch('/api/generate-video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          image: imageUrl,
          model: selectedModel,
          duration,
          aspectRatio,
          resolution: model?.options.resolution ? resolution : undefined,
          generateAudio,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error generating video');
      }

      toast.success(
        'Video generation started! Check the AI Videos page to see the result.',
      );
      onClose();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Modal
      showModal={isOpen}
      onClose={onClose}
      className='mt-5 overflow-y-auto sm:max-w-2xl'
    >
      <div className='px-4 py-5 sm:p-6'>
        <h3 className='text-lg font-medium leading-6 text-gray-900 dark:text-white'>
          {t('Generate Video from Image')}
        </h3>

        <div className='mt-5 space-y-6'>
          {/* Image Preview */}
          <div className='relative mx-auto w-full max-w-xs overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-white/5 dark:bg-slate-800'>
            <img
              src={imageUrl}
              alt={t('Source image')}
              className='h-auto w-full object-cover'
              loading='lazy'
            />
          </div>

          <div>
            <label
              htmlFor='model'
              className='flex items-center text-sm font-medium text-gray-700 dark:text-slate-400'
            >
              {t('Model')}
              <SimpleTooltip
                title={t('Different models produce different styles of videos')}
              >
                <QuestionMarkCircleIcon className='ml-1 h-4 w-4 text-gray-500' />
              </SimpleTooltip>
            </label>
            <div className='mt-1'>
              <VideoModelSelection
                onChange={setSelectedModel}
                selectedModel={selectedModel}
                hasImage={true}
              />
            </div>
          </div>

          <VideoOptions
            selectedModel={selectedModel}
            duration={duration}
            aspectRatio={aspectRatio}
            resolution={resolution}
            generateAudio={generateAudio}
            onDurationChange={setDuration}
            onAspectRatioChange={setAspectRatio}
            onResolutionChange={setResolution}
            onGenerateAudioChange={setGenerateAudio}
            hasImage={true}
          />

          <div>
            <div className='flex items-center justify-between'>
              <label
                htmlFor='prompt'
                className='block text-sm font-medium text-gray-700 dark:text-slate-400'
              >
                {t('Describe the video you want to generate')}
              </label>
              <span
                className={`text-xs ${prompt.length > maxPromptLength - 100 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}
              >
                {prompt.length} / {maxPromptLength}
              </span>
            </div>
            <div className='mt-1'>
              <ReactTextareaAutosize
                id='prompt'
                name='prompt'
                rows={4}
                minRows={4}
                className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white sm:text-sm'
                value={prompt}
                onChange={(e) => {
                  const value = e.target.value;
                  // Clamp new input to the cap, but never below the current length
                  // so the user can still delete/edit down when the prompt is
                  // already over a newly-lowered per-model limit (e.g. after
                  // switching from a 5000- to a 1500-char model). Without this the
                  // textarea freezes: deletions produce an over-cap value and get
                  // reverted. (No native maxLength — it would block paste-reductions
                  // while over the cap.)
                  const limit = Math.max(maxPromptLength, prompt.length);
                  setPrompt(value.slice(0, limit));
                }}
                placeholder={t(
                  'Describe how you want the image to be animated',
                )}
              />
            </div>
          </div>

          <div className='mt-5 flex justify-end gap-x-3'>
            <Button variant='light' onClick={onClose}>
              {t('Cancel')}
            </Button>
            {isFreePlan ? (
              <ButtonLink href='/settings/billing' variant='dark'>
                {t('Upgrade plan')}
              </ButtonLink>
            ) : (
              <Button
                variant='dark'
                onClick={handleGenerate}
                isLoading={isGenerating}
                disabled={isGenerating || !prompt}
              >
                <span>
                  🤖 {t('Generate Video')} ({selectedModelCredits}{' '}
                  {t('credits')})
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
